import { CSSProperties, ReactNode, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface SortableListConfig<T> {
  id: string;
  header?: ReactNode;
  items: T[];
}

export interface DualSortableListsProps<T> {
  lists: [SortableListConfig<T>, SortableListConfig<T>];
  getItemId: (item: T) => string | number;
  renderItem: (item: T, listId: string) => ReactNode;
  onChange: (next: Record<string, T[]>) => void;
  emptyText?: (listId: string) => ReactNode;
  className?: string;
  listClassName?: string;
}

interface SortableRowProps {
  id: string;
  children: ReactNode;
}

const SortableRow = ({ id, children }: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  // Dynamic transform/transition come from dnd-kit at runtime, so they stay
  // inline; opacity is toggled via a Tailwind class.
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'opacity-50' : undefined}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
};

interface DroppableAreaProps {
  id: string;
  className?: string;
  children: ReactNode;
}

const DroppableArea = ({ id, className, children }: DroppableAreaProps) => {
  const { setNodeRef } = useSortable({ id, data: { isContainer: true } });
  return (
    <div ref={setNodeRef} className={className}>
      {children}
    </div>
  );
};

export const DualSortableLists = <T,>({
  lists,
  getItemId,
  renderItem,
  onChange,
  emptyText,
  className,
  listClassName,
}: DualSortableListsProps<T>) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [activeId, setActiveId] = useState<string | null>(null);

  const idOf = (item: T) => String(getItemId(item));
  const listIdByItemId = new Map<string, string>();
  const itemByItemId = new Map<string, T>();
  for (const list of lists) {
    for (const item of list.items) {
      listIdByItemId.set(idOf(item), list.id);
      itemByItemId.set(idOf(item), item);
    }
  }

  const findListId = (droppableOrItemId: string): string | undefined => {
    if (lists.some((l) => l.id === droppableOrItemId)) {
      return droppableOrItemId;
    }
    return listIdByItemId.get(droppableOrItemId);
  };

  const onDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const onDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) {
      return;
    }
    const activeItemId = String(active.id);
    const overId = String(over.id);
    const fromListId = listIdByItemId.get(activeItemId);
    const toListId = findListId(overId);
    if (!fromListId || !toListId) {
      return;
    }

    const next: Record<string, T[]> = {};
    for (const list of lists) {
      next[list.id] = [...list.items];
    }

    const fromItems = next[fromListId];
    const fromIndex = fromItems.findIndex((i) => idOf(i) === activeItemId);
    if (fromIndex === -1) {
      return;
    }
    const [moved] = fromItems.splice(fromIndex, 1);

    const toItems = next[toListId];
    const overIndex = toItems.findIndex((i) => idOf(i) === overId);
    const insertAt = overIndex === -1 ? toItems.length : overIndex;

    if (fromListId === toListId && fromIndex === insertAt) {
      return;
    }
    toItems.splice(insertAt, 0, moved);

    onChange(next);
  };

  const activeItem = activeId ? itemByItemId.get(activeId) : undefined;
  const activeListId = activeId ? listIdByItemId.get(activeId) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className={`flex items-start gap-4 ${className ?? ''}`}>
        {lists.map((list) => (
          <div key={list.id} className="min-w-[260px] flex-1">
            {list.header}
            <SortableContext
              id={list.id}
              items={list.items.map((i) => idOf(i))}
              strategy={verticalListSortingStrategy}
            >
              <DroppableArea id={list.id} className={listClassName}>
                {list.items.length === 0 && emptyText ? emptyText(list.id) : null}
                {list.items.map((item) => (
                  <SortableRow key={idOf(item)} id={idOf(item)}>
                    {renderItem(item, list.id)}
                  </SortableRow>
                ))}
              </DroppableArea>
            </SortableContext>
          </div>
        ))}
      </div>
      <DragOverlay>
        {activeItem && activeListId ? renderItem(activeItem, activeListId) : null}
      </DragOverlay>
    </DndContext>
  );
};
